/**
 * Shared request lifecycle — generalized from the Guided Planning / Progress
 * protections so NO user-facing network load can spin forever.
 *
 *   - finite timeout: every request gets one (api.js applies READ_TIMEOUT_MS
 *     to safe reads and WRITE_TIMEOUT_MS to writes unless the call passes its
 *     own `timeout`);
 *   - one automatic retry, ONLY for safe/idempotent reads (GET/HEAD, or a
 *     call explicitly marked `idempotent: true`) and ONLY on a transport
 *     failure (timeout / network error with no server response). Never an
 *     HTTP 4xx/5xx (those are answers), never a cancellation, never a
 *     POST/PATCH/PUT/DELETE or AI generation;
 *   - cancellation: callers pass an AbortController `signal`; a superseded or
 *     unmounted screen aborts it, and an aborted request is never retried;
 *   - stale guard: useLatestRequest() lets a screen ignore any response that
 *     is not from its newest request.
 *
 * Pure helpers here; api.js wires them into the axios instance.
 */
import { useCallback, useEffect, useMemo, useRef } from 'react';

// Safe reads: generous for slow mobile networks, then ONE retry (so a stalled
// read surfaces a recoverable error within ~2x this).
export var READ_TIMEOUT_MS = 15000;
// Writes are never retried automatically; the boundary only guarantees the
// spinner ends in a recoverable error (server-side idempotency still applies).
export var WRITE_TIMEOUT_MS = 90000;

// Read-only but EXPENSIVE computations sent as POST (candidate searches,
// optimizers, swap suggestions): finite and shorter than a write, but never
// re-executed automatically (retry:false) — explicit user Retry only.
export var COMPUTE_TIMEOUT_MS = 30000;

var SAFE_METHODS = { get: true, head: true, options: true };

/** The request was cancelled on purpose (superseded / left / unmounted). */
export function isAbortError(err) {
  return !!err && (err.name === 'CanceledError' || err.name === 'AbortError' || err.code === 'ERR_CANCELED' ||
    (typeof err.__CANCEL__ !== 'undefined' && err.__CANCEL__ === true));
}

/**
 * Known-offline short circuit: a safe read attempted while the device reports
 * `navigator.onLine === false` fails IMMEDIATELY with this error instead of
 * waiting out the read timeout (and is never auto-retried). `onLine === true`
 * proves nothing, so it never short-circuits anything.
 */
export var OFFLINE_ERROR_CODE = 'ERR_OFFLINE';

export function isKnownOffline() {
  try {
    return typeof navigator !== 'undefined' && navigator.onLine === false;
  } catch (_e) {
    return false;
  }
}

export function makeOfflineError(config) {
  var err = new Error('No internet connection');
  err.name = 'OfflineError';
  err.code = OFFLINE_ERROR_CODE;
  err.isOffline = true;
  err.config = config;
  return err;
}

export function isOfflineError(err) {
  return !!err && (err.code === OFFLINE_ERROR_CODE || err.isOffline === true);
}

/** The request hit its timeout boundary. */
export function isTimeoutError(err) {
  return !!err && (err.code === 'ECONNABORTED' || err.code === 'ETIMEDOUT' ||
    /timeout|timed out/i.test(String(err.message || '')));
}

/**
 * Worth ONE automatic retry for an idempotent read: a timeout or a network
 * failure that never produced a server response. Never a cancellation and
 * never an HTTP error response (4xx / 5xx).
 */
export function isRetryableReadError(err) {
  if (!err || isAbortError(err) || isOfflineError(err)) return false;
  if (err.response) return false;
  return isTimeoutError(err) || err.code === 'ERR_NETWORK' || /network/i.test(String(err.message || ''));
}

/** A request config that is a safe/idempotent read (eligible for one retry). */
export function isSafeReadConfig(config) {
  if (!config) return false;
  var method = String(config.method || 'get').toLowerCase();
  return !!SAFE_METHODS[method] || config.idempotent === true;
}

/** The default timeout for a config that did not set one. */
export function defaultTimeoutFor(config) {
  return isSafeReadConfig(config) ? READ_TIMEOUT_MS : WRITE_TIMEOUT_MS;
}

/** Should the shared layer re-send this failed request once? */
export function shouldRetryOnce(config, err) {
  if (!config || config.retry === false || config.__fxRetried) return false;
  if (config.signal && config.signal.aborted) return false;
  return isSafeReadConfig(config) && isRetryableReadError(err);
}

/** A fresh AbortController, or a signal-less stand-in where none exists. */
export function newRequestController() {
  if (typeof AbortController === 'function') return new AbortController();
  return { signal: undefined, abort: function () {} };
}

/**
 * axios config fragment for a lifecycle-managed call:
 *   { signal, timeout, retry: false (opt out of the shared retry), idempotent }
 */
export function lifecycleConfig(opts) {
  var config = {};
  if (opts && opts.signal) config.signal = opts.signal;
  if (opts && opts.timeout) config.timeout = opts.timeout;
  if (opts && opts.retry === false) config.retry = false;
  if (opts && opts.idempotent === true) config.idempotent = true;
  return config;
}

/**
 * Per-screen "latest request wins". begin() aborts the previous request and
 * returns { signal, isCurrent() }; responses for anything but the newest
 * request must be ignored (isCurrent() === false). Aborts on unmount. The
 * returned object is referentially stable (safe in dependency arrays).
 */
export function useLatestRequest() {
  var ref = useRef(null);
  var cancel = useCallback(function () {
    if (ref.current) {
      ref.current.cancelled = true;
      ref.current.controller.abort();
    }
    ref.current = null;
  }, []);
  var begin = useCallback(function () {
    cancel();
    var entry = { controller: newRequestController(), cancelled: false };
    ref.current = entry;
    return {
      signal: entry.controller.signal,
      isCurrent: function () { return !entry.cancelled && ref.current === entry; },
    };
  }, [cancel]);
  useEffect(function () { return cancel; }, [cancel]);
  // STABLE identity across renders: screens list it in hook dependencies.
  return useMemo(function () { return { begin: begin, cancel: cancel }; }, [begin, cancel]);
}

/**
 * An AMBIGUOUS transport failure (timeout / network, no server response): the
 * server may or may not have received the request. Only a request carrying
 * its own idempotency key (e.g. POST /ai-log/jobs with the same
 * submission_id) may be re-sent after one of these — and only explicitly.
 */
export function isTransportFailure(err) {
  return isRetryableReadError(err);
}
